/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2019, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS 'AS IS' AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'

import moment from 'moment'

import {
  Button,
  SvgIcon,
  WorkflowRunStepAssignedTo,
  PayrollCycleName,
  IdentitiesWithResponsibilities,
} from '../'

import {
  decodeBase64,
  hasResponsibility
} from '../../utils'

const approve = (stepId, refresh, readyNextSteps) => {
  let body = new FormData()
  body.append('workflowrunstep', decodeBase64(stepId))
  if (readyNextSteps > 0) {
    body.append('ready_nextsteps', readyNextSteps)  // optional parameter that immediately sets n amount of subsequent workflowrunsteps to ready
  }

  fetch(`${process.env.REACT_APP_API_ROOT}/v1/workflowrunstepapprove/`, {
    method: 'post',
    headers: {
      "Authorization": "Token " + localStorage.getItem('token') || null
    },
    body: body
  })
  .then((data) => {
    if (data.ok) {  // covers 200-range of http responses
      console.log("Deduction amount/payment approved.")
      refresh()
    } else {
      console.log(`Deduction amount/payment approval request failed.  HTTP: ${data.status} - ${data.statusText}`)
    }
    return data.json()
  })
  .catch((error) => {
    console.log('Deduction amount/payment approval request failed', error)
  })
}

export default class DeductionHeader extends React.Component {
  static propTypes = {
    deduction: PropTypes.object.isRequired,
    payrollDate: PropTypes.string.isRequired,
    payrollCycleId: PropTypes.string.isRequired,
    refresh: PropTypes.func.isRequired,
    readOnly: PropTypes.bool
  }

  static defaultProps = {
    readOnly: false
  }

  state = {
    userIsDeductionApprover: hasResponsibility("self", "internal_review_deductions"),
    userIsAmountApprover: hasResponsibility("self", "deductions_approver_amounts", "payrollcycle", decodeBase64(this.props.payrollCycleId)),
    userIsPaymentApprover: hasResponsibility("self", "deductions_approver_payment", "payrollcycle", decodeBase64(this.props.payrollCycleId))
  }

  internalDeductionApproval = (workflowRunStep6, workflowRunStep10) => {
    let workflowRunDeducting = this.props.deduction

    let workflowRunStep5 = workflowRunDeducting.workflowrunsteps.edges.find((workflowrunstep) => (
      workflowrunstep.node.workflowstep.number === 5
    )).node

    let deductionsApproved = workflowRunStep6.status === "PROCESSED"
    let paymentApproved = workflowRunStep10.status === "PROCESSED"

    return (
      <div className={`slds-grid slds-p-left_medium slds-p-right_x-large slds-p-top_large
        slds-p-bottom_medium deduction-header__status${paymentApproved ? " is-approved" : ''}`}>
        <div className="slds-size_1-of-12 slds-align-top">
          {deductionsApproved &&
            <SvgIcon
              variant="utility"
              symbol="check"
              size="small"
              colour={paymentApproved ? "inverse" : "success"}
            />
          }
        </div>
        {workflowRunDeducting.status === "PROCESSING" &&
          workflowRunStep5.status === "PROCESSED" &&
          workflowRunStep6.status === "READY" &&
          <div className="slds-size_11-of-12" style={{ color: "white" }}>
            <Button
              type="brand"
              onClick={() => approve(workflowRunStep6.id, this.props.refresh)}>
              APPROVE DEDUCTIONS
            </Button>
          </div>
        }
      </div>
    )
  }

  deductionAmountApproval = (payrollCycleUuid, workflowRunStep6, workflowRunStep8, workflowRunStep10) => {
    let deduction = this.props.deduction

    let amountApprovalPending = deduction.atStep <= 8 &&
      ["PENDING","READY"].indexOf(workflowRunStep8.status) > -1
    let deductionApproved = workflowRunStep6.status === "PROCESSED"
    let amountApproved = workflowRunStep8.status === "PROCESSED"
    let paymentApproved = workflowRunStep10.status === "PROCESSED"

    return (
      <div className={`slds-grid slds-p-left_medium slds-p-right_x-large slds-p-bottom_medium
        slds-p-top_large deduction-header__status${paymentApproved ? " is-approved" : ''}`}>
        <div className="slds-size_1-of-12 slds-align-top">
          {amountApproved &&
            <SvgIcon
              variant="utility"
              symbol="check"
              size="small"
              colour={paymentApproved ? "inverse" : "success"}
            />
          }
        </div>
        {deductionApproved
          ? this.state.userIsAmountApprover
            ? amountApproved
              ? <div
                  className="slds-size_11-of-12"
                  style={{
                    color: amountApprovalPending
                      ? "orange"
                      : paymentApproved ? "white" : "#1d9652" }}>
                  <div className="side-panel__title">
                    Amount approved
                  </div>
                  <div className="side-panel__user">
                    by <WorkflowRunStepAssignedTo
                      workflowRunId={deduction.id}
                      workflowRunStep={8}
                    />
                  </div>
                </div>
              : <div className="slds-text-align_right">
                  {/* <Button onClick={this.props.showQueryModal} className="slds-m-right_medium">
                    QUERY
                  </Button> */}
                  <Button
                    type="brand"
                    disabled={!this.state.userIsAmountApprover}
                    onClick={() => approve(workflowRunStep8.id, this.props.refresh, 2)}>  {/* readying next two workflowrun steps */}
                    APPROVE AMOUNT  {/* approve payment amount */}
                  </Button>
                </div>
            : <div
                className="slds-size_11-of-12"
                style={{
                  color: amountApprovalPending
                    ? "orange"
                    : paymentApproved ? "white" : "#1d9652" }}>
                <div className="side-panel__title">
                  {amountApprovalPending
                    ? "Waiting for amount approval"
                    : "Amount approved"
                  }
                </div>
                <div className="side-panel__user slds-is-relative">
                  by&nbsp;
                  {amountApprovalPending
                    ? <IdentitiesWithResponsibilities
                        responsibilities={[
                          ["deductions_approver_amounts", "payrollcycle", payrollCycleUuid]
                        ]}
                    />
                    : <WorkflowRunStepAssignedTo
                        workflowRunId={deduction.id}
                        workflowRunStep={8}
                      />
                  }
                </div>
              </div>
          : <div className="slds-size_11-of-12" style={{ color:"orange" }}>
              <div className="side-panel__title">
                Waiting for amount approval
              </div>
              <div className="side-panel__user slds-is-relative">
                by <IdentitiesWithResponsibilities
                  responsibilities={[
                    ["deductions_approver_amounts", "payrollcycle", payrollCycleUuid]
                  ]}
                />
              </div>
            </div>
        }
      </div>
    )
  }

  deductionPaymentApproval = (payrollCycleUuid, workflowRunStep6, workflowRunStep8, workflowRunStep10) => {
    let deduction = this.props.deduction

    let paymentApprovalPending = deduction.atStep <= 10 &&
      ["PENDING","READY"].indexOf(workflowRunStep10.status) > -1
    let deductionApproved = workflowRunStep6.status === "PROCESSED"
    let amountApproved = workflowRunStep8.status === "PROCESSED"
    let paymentApproved = workflowRunStep10.status === "PROCESSED"

    return (
      <div className={`slds-grid slds-p-left_medium slds-p-right_x-large slds-p-bottom_medium
        deduction-header__status${paymentApproved ? " is-approved" : ''}`}>
        <div className="slds-size_1-of-12 slds-align-top">
          {paymentApproved &&
            <SvgIcon
              variant="utility"
              symbol="check"
              size="small"
              colour={paymentApproved ? "inverse" : "success"}
            />
          }
        </div>
        {deductionApproved
          ? deduction.status === "PROCESSING" &&
            amountApproved &&
            workflowRunStep10.status === "READY"
            ? this.state.userIsPaymentApprover
              ? <div className="slds-size_11-of-12" style={{ color: "white" }}>
                  <Button
                    type="brand"
                    onClick={() => approve(workflowRunStep10.id, this.props.refresh)}>
                    APPROVE PAYMENT
                  </Button>
                </div>
              : <div className="slds-size_11-of-12" style={{ color: paymentApprovalPending ? "orange" : "white" }}>
                  <div className="side-panel__title">
                    {paymentApprovalPending
                      ? "Waiting for payment approval"
                      : "Payment approved"
                    }
                  </div>
                  <div className="side-panel__user slds-is-relative">
                    by&nbsp;
                    {paymentApprovalPending
                      ? <IdentitiesWithResponsibilities
                          responsibilities={[
                            ["deductions_approver_payment", "payrollcycle", payrollCycleUuid]
                          ]}
                        />
                      : <WorkflowRunStepAssignedTo
                          workflowRunId={deduction.id}
                          workflowRunStep={8}
                        />
                    }
                  </div>
                </div>
            : paymentApproved
              ? <div className="slds-size_11-of-12" style={{ color: paymentApprovalPending ? "orange" : "white" }}>
                  <div className="side-panel__title">
                    Payment approved
                  </div>
                  <div className="side-panel__user">
                    by <WorkflowRunStepAssignedTo
                      workflowRunId={deduction.id}
                      workflowRunStep={10}
                    />
                  </div>
                </div>
              : <div className="slds-size_11-of-12" style={{ color: paymentApprovalPending ? "orange" : "white" }}>
                  <div className="side-panel__title">
                    {paymentApprovalPending
                      ? "Waiting for payment approval"
                      : "Payment approved"
                    }
                  </div>
                  <div className="side-panel__user slds-is-relative">
                    by&nbsp;
                    {paymentApprovalPending
                      ? <IdentitiesWithResponsibilities
                          responsibilities={[
                            ["deductions_approver_payment", "payrollcycle", payrollCycleUuid]
                          ]}
                        />
                      : <WorkflowRunStepAssignedTo
                          workflowRunId={deduction.id}
                          workflowRunStep={8}
                        />
                    }
                  </div>
                </div>
          : <div className="slds-size_11-of-12" style={{ color: "orange" }}>
              <div className="side-panel__title">
                Waiting for payment approval
              </div>
              <div className="side-panel__user">
                by <IdentitiesWithResponsibilities
                  responsibilities={[
                    ["deductions_approver_payment", "payrollcycle", payrollCycleUuid]
                  ]}
                />
              </div>
            </div>
        }
      </div>
    )
  }

  render() {
    let deduction = this.props.deduction
    let payrollCycleUuid = decodeBase64(this.props.payrollCycleId)

    let workflowRunStep6 = deduction.workflowrunsteps.edges.find(({ node: workflowRunStep }) => (
      workflowRunStep.workflowstep.number === 6
    )).node
    let workflowRunStep8 = deduction.workflowrunsteps.edges.find(({ node: workflowRunStep }) => (
      workflowRunStep.workflowstep.number === 8
    )).node
    let workflowRunStep10 = deduction.workflowrunsteps.edges.find(({ node: workflowRunStep }) => (
      workflowRunStep.workflowstep.number === 10
    )).node

    return (
      <div className="slds-grid slds-wrap deduction-header">
        <div className="slds-col slds-p-top_large slds-p-left_medium slds-p-bottom_small slds-p-right_medium">
          <div className="slds-text-title_caps">
            {`FOR ${moment(this.props.payrollDate).format("DD MMM YYYY")}`}
          </div>
          <div className="slds-text-heading_medium u-no-margin__b">
            <b>
              <PayrollCycleName payrollCycleId={this.props.payrollCycleId} />
            </b>
          </div>
        </div>
        <div className="slds-col u-flex-align-self side-panel">
          <div>
            {this.state.userIsDeductionApprover &&
              deduction.atStep === 6 &&
              this.internalDeductionApproval(payrollCycleUuid, workflowRunStep6, workflowRunStep10)
            }
            {this.deductionAmountApproval(payrollCycleUuid, workflowRunStep6, workflowRunStep8, workflowRunStep10)}
            {this.deductionPaymentApproval(payrollCycleUuid, workflowRunStep6, workflowRunStep8, workflowRunStep10)}
          </div>
        </div>
      </div>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/deduction-header/index.js